import { create } from "zustand";
import { persist } from "zustand/middleware";

import { UserTiny } from "@/types/user";

const MAX_HISTORY = 5;

interface RecentItemsState {
  recentPatients: Record<string, UserTiny[]>; // tenant -> patients mapping
  recentPages: string[]; // global pages (not tenant-specific)
  updateRecentPatients: (tenant: string, patient: UserTiny) => void;
  getRecentPatients: (tenant: string) => UserTiny[];
  clearRecentPatients: (tenant: string) => void;
  updateRecentPages: (page: string) => void;
  getRecentPages: () => string[];
  clearRecentPages: () => void;
}

export const useRecentItemsStore = create<RecentItemsState>()(
  persist(
    (set, get) => ({
      recentPatients: {},
      recentPages: [],

      updateRecentPatients: (tenant: string, patient: UserTiny) => {
        set((state) => {
          const currentPatients = state.recentPatients[tenant] || [];
          const updatedPatients = [
            patient,
            ...currentPatients
              .filter((p) => p.id !== patient.id)
              .slice(0, MAX_HISTORY - 1),
          ];

          return {
            ...state,
            recentPatients: {
              ...state.recentPatients,
              [tenant]: updatedPatients,
            },
          };
        });
      },

      getRecentPatients: (tenant: string) => {
        const state = get();
        return state.recentPatients[tenant] || [];
      },

      clearRecentPatients: (tenant: string) => {
        set((state) => ({
          ...state,
          recentPatients: {
            ...state.recentPatients,
            [tenant]: [],
          },
        }));
      },

      updateRecentPages: (page: string) => {
        set((state) => {
          const updatedPages = [
            page,
            ...state.recentPages
              .filter((p) => p !== page)
              .slice(0, MAX_HISTORY - 1),
          ];

          return {
            ...state,
            recentPages: updatedPages,
          };
        });
      },

      getRecentPages: () => {
        const state = get();
        return state.recentPages;
      },

      clearRecentPages: () => {
        set((state) => ({
          ...state,
          recentPages: [],
        }));
      },
    }),
    {
      name: "recent-items-storage",
      partialize: (state) => ({
        recentPatients: state.recentPatients,
        recentPages: state.recentPages,
      }),
    },
  ),
);

// Keep the old export for backward compatibility during migration
export const useRecentPatientsStore = useRecentItemsStore;
